import { useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import type { CreatedWebhook } from './types';

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 10001,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const MODAL_STYLE: CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  maxWidth: '640px',
  width: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const CODE_STYLE: CSSProperties = {
  display: 'block',
  padding: '14px 16px',
  background: '#0f172a',
  color: '#e2e8f0',
  borderRadius: '6px',
  fontSize: '12px',
  fontFamily: 'monospace',
  lineHeight: '1.6',
  whiteSpace: 'pre',
  overflowX: 'auto',
  margin: '8px 0 0',
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copiado!' : 'Copiar'}
      style={{
        border: 'none',
        background: copied ? '#dcfce7' : '#f3f4f6',
        color: copied ? '#16a34a' : '#374151',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '12px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 500,
        transition: 'background 0.15s',
      }}
      aria-label={copied ? 'Copiado' : 'Copiar para área de transferência'}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copiado!' : 'Copiar'}
    </button>
  );
}

export interface PostCreationModalProps {
  webhook: CreatedWebhook;
  onClose: () => void;
}

export default function PostCreationModal({ webhook, onClose }: PostCreationModalProps) {
  const [tab, setTab] = useState<'verify-node' | 'verify-curl'>('verify-node');

  const nodeExample = `// Verify the signature on each incoming webhook (Node.js / Bun / Deno).
// Header: X-Joshua-Signature: t=<unix_ts>,v1=<hex_hmac>
import crypto from 'node:crypto';

const SECRET = process.env.JOSHUA_WEBHOOK_SECRET; // ${webhook.secret.slice(0, 12)}…

export function verify(body, signatureHeader) {
  const m = signatureHeader.match(/t=(\\d+),v1=([0-9a-f]+)/);
  if (!m) return false;
  const [, ts, sig] = m;

  // Reject if older than 5 minutes (replay defense)
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false;

  const expected = crypto
    .createHmac('sha256', SECRET)
    .update(\`\${ts}.\${body}\`)
    .digest('hex');

  return crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected));
}`;

  const curlExample = `# Inspect what arrives — useful while wiring up your endpoint.
# X-Joshua-Event       — event type (e.g. obra.updated)
# X-Joshua-Delivery    — uuid for idempotency
# X-Joshua-Webhook-Id  — your webhook subscription id
# X-Joshua-Signature   — t=<ts>,v1=<hex>

curl -i -X POST https://your-server.example.com/webhook \\
  -H "Content-Type: application/json" \\
  -H "X-Joshua-Event: obra.updated" \\
  -H "X-Joshua-Delivery: $(uuidgen)" \\
  -H "X-Joshua-Webhook-Id: ${webhook.id}" \\
  -H "X-Joshua-Signature: t=$(date +%s),v1=<hex>" \\
  -d '{"event":"obra.updated","payload":{"id":"…"}}'`;

  return createPortal(
    <div
      style={BACKDROP_STYLE}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={MODAL_STYLE} role="dialog" aria-modal="true" aria-label="Webhook criado com sucesso">

        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--panel-border, #e5e7eb)',
          background: '#f0f9ff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Check size={18} color="#16a34a" />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#154680' }}>
                Webhook criado
              </h2>
              <p style={{
                margin: '2px 0 0',
                fontSize: '12px',
                color: '#6b7280',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {webhook.url}
              </p>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px 14px',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: '1px' }} />
            <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#78350f' }}>
              <strong>Esta é a única vez que o secret será exibido.</strong>
              {' '}Copie e configure no seu servidor agora. O secret é usado para validar assinaturas HMAC
              em cada entrega — sem ele, o receiver não consegue distinguir requisições legítimas de tráfego adversário.
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px',
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}>
                Signing Secret
              </label>
              <CopyButton text={webhook.secret} />
            </div>
            <div style={{
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1.5px solid #154680',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '13px',
              color: '#154680',
              wordBreak: 'break-all',
              lineHeight: '1.5',
            }}>
              {webhook.secret}
            </div>
          </div>

          <div>
            <p style={{
              margin: '0 0 10px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
            }}>
              Validação no seu servidor
            </p>

            <div style={{
              display: 'flex',
              gap: '2px',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '12px',
            }}>
              {(['verify-node', 'verify-curl'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: tab === t ? '#154680' : '#6b7280',
                    borderBottom: tab === t ? '2px solid #154680' : '2px solid transparent',
                    marginBottom: '-2px',
                    transition: 'color 0.15s',
                  }}
                >
                  {t === 'verify-node' ? 'Node.js (verify)' : 'curl (preview)'}
                </button>
              ))}
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '18px', right: '10px', zIndex: 1 }}>
                <CopyButton text={tab === 'verify-node' ? nodeExample : curlExample} />
              </div>
              <code style={CODE_STYLE}>
                {tab === 'verify-node' ? nodeExample : curlExample}
              </code>
            </div>
          </div>
        </div>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--panel-border, #e5e7eb)',
          display: 'flex',
          justifyContent: 'flex-end',
          background: '#f9fafb',
        }}>
          <button className="btn-add undo-animated" onClick={onClose}>
            <UndoAnimatedIcon size={15} />
            <span className="btn-text">Fechar</span>
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
